import React, { useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import {
  CHECK_KIND_GROUP,
  CHECK_KIND_LEAF,
  CheckExpression,
  CheckGroup,
  CheckLeaf,
  ConditionNodeData,
  OPERATOR_AND,
  WorkflowNode
} from "lib/types/workflow"
import Input from "components/Ui/Form/Input"
import {
  FormSection,
  SaveButton,
  SmallActionButton,
  ValidationProblems,
  ValidationProblemsList
} from "./NodeEditorSidebar.styled"
import LeafCheckEditor from "./LeafCheckEditor"
import CheckGroupEditor from "./CheckGroupEditor"
import { getExpressionProblems, isLeafValid } from "./conditionFormHelpers"
import { makeDefaultLeaf } from "./checkFieldHelpers"

type Props = {
  node: WorkflowNode
  onSave: (nodeId: string, data: ConditionNodeData) => void
}

interface FormValues {
  label: string
}

const isExpressionValid = (expr: CheckExpression): boolean => {
  if (expr.kind === CHECK_KIND_LEAF) return isLeafValid(expr)
  if (expr.children.length < 2) return false
  return expr.children.every(isExpressionValid)
}

const ConditionNodeForm = ({ node, onSave }: Props) => {
  const data = node.data as ConditionNodeData

  const [expression, setExpression] = useState<CheckExpression>(
    () => data.expression ?? makeDefaultLeaf()
  )

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<FormValues>({
    defaultValues: { label: data.label }
  })

  const promoteToGroup = () => {
    if (expression.kind !== CHECK_KIND_LEAF) return
    const group: CheckGroup = {
      kind: CHECK_KIND_GROUP,
      operator: OPERATOR_AND,
      children: [expression, makeDefaultLeaf()]
    }
    setExpression(group)
  }

  const handleGroupChange = (next: CheckGroup) => setExpression(next)
  const handleGroupUnwrap = (child: CheckExpression) => setExpression(child)
  const handleLeafChange = (next: CheckLeaf) => setExpression(next)

  const isValid = useMemo(() => isExpressionValid(expression), [expression])
  const problems = useMemo(
    () => (isValid ? [] : getExpressionProblems(expression)),
    [expression, isValid]
  )
  const [showProblems, setShowProblems] = useState(false)

  const onSubmit = (values: FormValues) => {
    if (!isValid) {
      setShowProblems(true)
      return
    }
    onSave(node.id, {
      nodeType: "CONDITION",
      label: values.label,
      expression
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormSection>
        <Input
          name="label"
          label="Name"
          register={register}
          validation={{
            required: "Name is required",
            maxLength: {
              value: 100,
              message: "Name must be at most 100 characters"
            }
          }}
          errors={errors}
        />
      </FormSection>

      {expression.kind === CHECK_KIND_LEAF ? (
        <>
          <LeafCheckEditor value={expression} onChange={handleLeafChange} />
          <FormSection>
            <SmallActionButton type="button" onClick={promoteToGroup}>
              + Add check (combine with AND/OR)
            </SmallActionButton>
          </FormSection>
        </>
      ) : (
        <CheckGroupEditor
          value={expression}
          onChange={handleGroupChange}
          onUnwrap={handleGroupUnwrap}
        />
      )}

      {showProblems && problems.length > 0 && (
        <ValidationProblems role="alert">
          <strong>Fix the following before saving:</strong>
          <ValidationProblemsList>
            {problems.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ValidationProblemsList>
        </ValidationProblems>
      )}

      <SaveButton type="submit">Update Node</SaveButton>
    </form>
  )
}

export default ConditionNodeForm
